import * as React from "react";
import { Routine } from "redux-saga-routines";
import { connect } from "react-redux";
import {Action, ActionFunctionAny} from "redux-actions";
import { DropdownWrap } from "@Elements/Form/DropdownWrap";
import {
  micAndSpeakerOptions,
  micVolumeOptions,
  dectSpeakerOptions,
  voicePromptVolumeOptions,
  micAndSpeakerOptionsSTCB
} from "./options";
import { ComponentWrapper, StyledForm, ButtonContainer, Footer } from "../styled";
import { useIntl } from "react-intl";
import { DeviceComponents } from "@Safemate/Settings/tabDefinition";
import { SettingEnum as ComponentSettingEnum } from "../../../settingEnum";
import { DeviceHardwareEnum, isGx8 } from "@Safemate/DeviceHardwareEnum";
import { Formik, useFormikContext } from "formik";
import SettingsHeader from "../../header";
import { CancelButton, SaveButton } from "../../../../DefaultSettings/Settings/styled";
import { saveSoundLight } from "../../../Store/routines";
import { SoundLightSettings } from "@Safemate/Settings/types";
import {
  SettingsLayout,
  Section,
  SectionTitle,
  SectionDesc,
  FieldGrid,
  CheckGrid,
  ToggleRow,
  Hint
} from "./settingsLayout";
import { CheckWrap } from "@Elements/Form/CheckWrap";
import { SettingEnum } from "@Safemate/DefaultSettings/Settings/settingEnum";
import { AppState } from "@Safemate/Store/types";

interface LightSoundSettingsProps{
  dehaId: number;
  deviId: number;
  soundLight: SoundLightSettings;
  saveSoundLight: Routine<ActionFunctionAny<Action<any>>>;
}

const LightSoundSettings = ({ dehaId, deviId, soundLight, saveSoundLight }: LightSoundSettingsProps) => {

  const components: Map<ComponentSettingEnum, JSX.Element[]> = DeviceComponents.has(dehaId) 
    ? DeviceComponents.get(dehaId)! 
    : DeviceComponents.get(DeviceHardwareEnum.DEFAULT)!;

  const { formatMessage } = useIntl();

  return(
    <Formik
      onSubmit={(values) => {
        saveSoundLight({
          deviceId: deviId,
          settings: values
        })
      }}
      initialValues={soundLight}
      enableReinitialize
    >
    {({ dirty, resetForm}) => 
      <StyledForm>
        <SettingsHeader 
          title={formatMessage({id: "sound", defaultMessage: "Lyd"})}
          tooltip="soundSettingsTabToolTipText"
          tooltipTitle={{
            id: "soundSettingsTab",
            defaultMessage: "Lydinnstillinger"
          }}
        />
        <SettingsLayout>
          {components.has(ComponentSettingEnum.GX8_SOUND) 
            && components.get(ComponentSettingEnum.GX8_SOUND)?.map((comp, i) => {
              if(isGx8(dehaId)){
                return <React.Fragment key={i}>{comp}</React.Fragment>
              }
              return <ComponentWrapper key={i}>{comp}</ComponentWrapper>
          })}
        </SettingsLayout>
        <Footer>
          <ButtonContainer disabled={!dirty}>
            <CancelButton disabled={!dirty} onClick={() => resetForm(soundLight)}>
              {formatMessage({id: "cancel", defaultMessage: "Avbryt"})}
            </CancelButton>
            <SaveButton disabled={!dirty} type="submit">
              {formatMessage({id: "save", defaultMessage: "Lagre"})}
            </SaveButton>
          </ButtonContainer>
        </Footer>
      </StyledForm>}
    </Formik>
  )
}

const mapStateToProps = ({ settings: {initialization: { deviId, dehaId }, general: { soundLight }}}: AppState) => {
  return {
    dehaId,
    deviId,
    soundLight
  }
}

const mapDispatchToProps = {
  saveSoundLight
}

export default connect(mapStateToProps, mapDispatchToProps)(LightSoundSettings);

const VolumeLevel = DropdownWrap(
  "volumeLevel",
  {
    title: {id: "soundVolumeParagraphTitle", defaultMessage: "Høytallervolum"},
    tooltip: "soundVolumeToolTipText",
    tooltipTitle: {
      id: "soundVolumeToolTipTitle",
      defaultMessage: "Høyttalervolum"
    },
    dataOptions: {
      options: micAndSpeakerOptions,
      skipMap: true
    },
    disableOptions: {
      name: SettingEnum.SYNCHRONIZE_SOUND,
      value: false
    },
    disabledAsControl: true,
    width: "100%"
  }
)

const TwoWayMic = DropdownWrap(
  "twoWayMic",
  {
    title: {id: "soundMicVolume", defaultMessage: "Mikrofonsensitivitet"},
    tooltip: "microphoneSensitivityToolTipText",
    tooltipTitle: {
      id: "microphoneSensitivityToolTipTitle",
      defaultMessage: "Mikrofonsensitivitet"
    },
    dataOptions: {
      options: micVolumeOptions,
      skipMap: true
    },
    disableOptions: {
      name: SettingEnum.SYNCHRONIZE_SOUND,
      value: false
    },
    disabledAsControl: true,
    width: "100%"
  }
)

const VolumeLevelSTCB = DropdownWrap(
  "volumeLevel",
  {
    title: {id: "soundVolumeParagraphTitle", defaultMessage: "Høytallervolum"},
    tooltip: "soundVolumeToolTipText",
    tooltipTitle: {
      id: "soundVolumeToolTipTitle",
      defaultMessage: "Høyttalervolum"
    },
    dataOptions: {
      options: micAndSpeakerOptionsSTCB,
      skipMap: true
    },
    disableOptions: {
      name: SettingEnum.SYNCHRONIZE_SOUND,
      value: false
    },
    disabledAsControl: true,
    width: "100%"
  }
)

const TwoWayMicSTCB = DropdownWrap(
  "twoWayMic",
  {
    title: {id: "soundMicVolume", defaultMessage: "Mikrofonsensitivitet"},
    tooltip: "microphoneSensitivityToolTipText",
    tooltipTitle: {
      id: "microphoneSensitivityToolTipTitle",
      defaultMessage: "Mikrofonsensitivitet"
    },
    dataOptions: {
      options: micAndSpeakerOptionsSTCB,
      skipMap: true
    },
    disableOptions: {
      name: SettingEnum.SYNCHRONIZE_SOUND,
      value: false
    },
    disabledAsControl: true,
    width: "100%"
  }
)

const DectVolume = DropdownWrap(
  "dectVolumeLevel",
  {
    title: {id: "soundDectVolumeToolTipTitle", defaultMessage: "DECT Høyttalervolum"},
    tooltip: "soundDectVolumeToolTipText",
    tooltipTitle: {
      id: "soundDectVolumeToolTipTitle",
      defaultMessage: "DECT Høyttalervolum"
    },
    dataOptions: {
      options: dectSpeakerOptions,
      skipMap: true
    },
    disableOptions: {
      name: SettingEnum.SYNCHRONIZE_SOUND,
      value: false
    },
    disabledAsControl: true,
    width: "100%"
  }
)

const VoiceVolume = DropdownWrap(
  "promptVol",
  {
    title: {id: "voicePromptVolumeToolTipTitle", defaultMessage: "Talemeldingvolum"},
    tooltip: "voicePromptVolumeToolTipText",
    tooltipTitle: {
      id: "voicePromptVolume",
      defaultMessage: "Talemeldingvolum"
    },
    dataOptions: {
      options: voicePromptVolumeOptions,
      skipMap: true
    },
    disableOptions: {
      name: SettingEnum.SYNCHRONIZE_SOUND,
      value: false
    },
    disabledAsControl: true,
    width: "100%"
  }
  )

export const ConfirmBeep =
  CheckWrap<SoundLightSettings>(
    "confirmBeep",
    {
      label: {
        id: "soundConfirmParagraphTitle",
        defaultMessage: "Kø lyd"
      },
      contents: true,
      tooltip: "soundConfirmToolTipText",
      tooltipTitle: {id: "soundConfirmToolTipTitle", defaultMessage: "Kø lyd"}
    }
  )

export const WandererBeep =
  CheckWrap<SoundLightSettings>(
    "wandererBeep",
    {
      label: {
        id: "wandererBeep",
        defaultMessage: "Vandre pling"
      },
      contents: true,
      tooltip: "wandererBeepTooltip",
      tooltipTitle: {id: "wandererBeep", defaultMessage: "Vandre pling"}
    }
  )

export const SynchronizeSound = 
    CheckWrap<SoundLightSettings>(
      SettingEnum.SYNCHRONIZE_SOUND,
      {
        label: {
          id: "synchronizeSound",
          defaultMessage: "Synkronisere lyd mellom server og enhet"
        },
        contents: true,
        tooltip: "synchronizeSoundTooltip",
        tooltipTitle: {id: "synchronizeSound", defaultMessage: "Synkronisere lyd mellom server og enhet"}
      }
    )

/* ── Layout ───────────────────────────────────────────────────────────── */

const useSyncEnabled = () => {
  const { values } = useFormikContext<SoundLightSettings>();
  return !!(values as any)[SettingEnum.SYNCHRONIZE_SOUND];
};

export const GX8SoundSyncSection = () => {
  const { formatMessage } = useIntl();
  return (
    <Section>
      <ToggleRow>
        <SynchronizeSound/>
      </ToggleRow>
      <SectionDesc style={{ margin: "10px 0 0" }}>
        {formatMessage({
          id: "soundSyncDescription",
          defaultMessage: "Når synkronisering er på, styres enhetens lydnivåer fra serveren. Er den av, beholder enheten sine egne innstillinger."
        })}
      </SectionDesc>
    </Section>
  )
}

export const GX8SoundVolumeSection = () => {
  const { formatMessage } = useIntl();
  const enabled = useSyncEnabled();
  return (
    <Section>
      <SectionTitle>
        {formatMessage({ id: "soundLevelsTitle", defaultMessage: "Lydnivåer" })}
      </SectionTitle>
      {!enabled &&
        <Hint>
          {formatMessage({
            id: "soundSyncDisabledHint",
            defaultMessage: "Slå på synkronisering for å justere lydnivåene."
          })}
        </Hint>}
      <FieldGrid dimmed={!enabled}>
        <VolumeLevel/>
        <TwoWayMic/>
        <DectVolume/>
        <VoiceVolume/>
      </FieldGrid>
    </Section>
  )
}

export const GX8SoundVolumeSectionSTCB = () => {
  const { formatMessage } = useIntl();
  const enabled = useSyncEnabled();
  return (
    <Section>
      <SectionTitle>
        {formatMessage({ id: "soundLevelsTitle", defaultMessage: "Lydnivåer" })}
      </SectionTitle>
      {!enabled &&
        <Hint>
          {formatMessage({
            id: "soundSyncDisabledHint",
            defaultMessage: "Slå på synkronisering for å justere lydnivåene."
          })}
        </Hint>}
      <FieldGrid dimmed={!enabled}>
        <VolumeLevelSTCB/>
        <TwoWayMicSTCB/>
      </FieldGrid>
    </Section>
  )
}

export const GX8SoundSignalSection = () => {
  const { formatMessage } = useIntl();
  return (
    <Section>
      <SectionTitle>
        {formatMessage({ id: "soundSignalsTitle", defaultMessage: "Varslingslyder" })}
      </SectionTitle>
      <CheckGrid>
        <ConfirmBeep/>
        <WandererBeep/>
      </CheckGrid>
    </Section>
  )
}
